// src/data/dashboardFocus.js
//
// THE FRONT PAGE'S ONE QUESTION. Bob, 2026-10-24: "that whole entire front page is bullshit and
// overwhelms the user, figure out a better opening presentation." The spec is
// data\YCP_FRONT_PAGE_REDESIGN_2026-10-24.md: zone 1 ACT, zone 2 WORK, zone 3 everything else.
//
// This module is zone 1 and nothing else: given what the Dashboard has already read, it says WHAT
// NEEDS THIS OFFICER, AND BY WHEN, in one ranked list. It computes no new fact - every row comes
// from a claim already on screen, a draft already loaded, or a deadline claimClocks already derives
// from the agreement's own clock (PD-4: a clock is a number in an agreement, never a default).
//
// TWO RULES IT MUST NOT BREAK:
//   1. An unanswered read is never an all-clear. If the claims query was refused, the caller passes
//      readFailed and this returns the REFUSED state - "Nothing needs you today" over a refused read
//      is the A1 defect (section 322 / the audit of 2026-09-22) wearing a calmer face.
//   2. A row is only shown to the officer who can act on it. The two General Chairperson rows are
//      gated on the role, as the stat cards they replace were (ruling 105 is not a licence to put
//      every officer's queue on every officer's screen).
//
// Pure. Proved by scripts\test-dashboard-focus.mjs.

import { appealDeadlines } from './claimClocks';

// Statuses that are NOT open work: retired, or not yet filed. Same list the Dashboard uses.
const NOT_OPEN = ['closure', 'archived', 'draft'];

export const FOCUS_EMPTY = 'Nothing needs you today.';
export const FOCUS_REFUSED = 'This list could not be built - the claims read was refused, so the platform does not know what needs you. Reload; if it happens twice, tell your General Chairperson.';

// How soon a deadline counts as "needs you now". A week is the shortest window in which an appeal
// can realistically be prepared and served; anything further out belongs on the Deadlines report.
export const SOON_DAYS = 7;

const iso = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : String(d || '').slice(0, 10));
const daysBetween = (fromIso, toIso) => Math.round(
  (Date.parse(toIso + 'T00:00:00Z') - Date.parse(fromIso + 'T00:00:00Z')) / 86400000
);

function isGC(user) {
  return !!(user && (user.role === 'general_chair' || user.isAdmin));
}

// The ranked list. Every item: { key, rank, severity, text, sub, page, arg }
//   severity: 'overdue' (a clock has run out) | 'due' (runs out within SOON_DAYS) | 'urgent' | 'waiting'
//   page/arg: where one click goes - the caller passes these straight to onNavigate.
export function needsYouNow(input) {
  const o = input || {};
  const user = o.user || null;
  if (!user) return { state: 'none', items: [] };
  if (o.readFailed) return { state: 'refused', items: [], message: FOCUS_REFUSED };

  const claims = Array.isArray(o.claims) ? o.claims : [];
  const drafts = Array.isArray(o.drafts) ? o.drafts : [];
  const today = iso(o.today || new Date());
  const deadlinesOf = typeof o.deadlinesOf === 'function' ? o.deadlinesOf : appealDeadlines;
  const elevationStage = o.elevationStage || null;
  const elevationLabel = o.elevationLabel || 'sent up';
  const items = [];

  // 1 and 2. The agreement's own clocks, on claims that are still live. Overdue first, then due soon.
  for (const c of claims) {
    if (NOT_OPEN.includes(c && c.status)) continue;
    let ds = [];
    try { ds = deadlinesOf(c) || []; } catch (e) { ds = []; }   // a claim whose agreement is not on record yields none
    for (const d of ds) {
      if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(String(d.date || ''))) continue;
      const left = daysBetween(today, d.date);
      if (left > SOON_DAYS) continue;
      items.push({
        key: 'clock:' + (d.claimId || c.docId || c.id) + ':' + d.date + ':' + (d.title || ''),
        rank: left < 0 ? 1 : 2,
        severity: left < 0 ? 'overdue' : 'due',
        days: left,
        text: left < 0
          ? 'Past due ' + Math.abs(left) + ' day' + (Math.abs(left) === 1 ? '' : 's') + ' - ' + (d.title || 'a deadline on this claim')
          : (left === 0 ? 'Due today' : 'Due in ' + left + ' day' + (left === 1 ? '' : 's')) + ' - ' + (d.title || 'a deadline on this claim'),
        sub: d.party ? (d.party === 'carrier' ? "the Carrier's time" : "our time") : '',
        page: 'claim-detail',
        arg: c.docId || d.claimId || null,
      });
    }
  }

  // 3. A draft that dies if nobody finishes it. The window is the platform's, not an agreement's.
  for (const d of drafts) {
    const left = Number(d && d.daysLeft);
    if (!Number.isFinite(left) || left > 5) continue;
    items.push({
      key: 'draft:' + (d.id || ''),
      rank: 3,
      severity: left <= 0 ? 'overdue' : 'due',
      days: left,
      text: left <= 0
        ? 'A draft claim has expired - ' + (d.claimantName || 'unnamed draft')
        : 'Draft claim expires in ' + left + ' day' + (left === 1 ? '' : 's') + ' - ' + (d.claimantName || 'unnamed draft'),
      sub: d.railroad || '',
      page: 'claims',
      arg: null,
    });
  }

  // 4. Urgent, as one row and not a card. The count is the door.
  const urgent = claims.filter((c) => c && c.priority === 'urgent' && !NOT_OPEN.includes(c.status)).length;
  if (urgent > 0) {
    items.push({
      key: 'urgent',
      rank: 4,
      severity: 'urgent',
      text: urgent + ' claim' + (urgent === 1 ? '' : 's') + ' marked urgent',
      sub: 'marked by the officer who filed or reviewed it',
      page: 'claims',
      arg: null,
    });
  }

  // 5 and 6. The two rows only a General Chairperson can act on.
  if (isGC(user) && elevationStage) {
    const elevated = claims.filter((c) => c && c.status === elevationStage).length;
    if (elevated > 0) {
      items.push({
        key: 'elevated',
        rank: 5,
        severity: 'waiting',
        text: elevated + ' claim' + (elevated === 1 ? '' : 's') + ' sent up to you',
        sub: 'waiting on your decision - your Local Chairperson cannot move it from here',
        page: 'claims',
        arg: { filterStatus: elevationLabel },
      });
    }
  }
  if (isGC(user) && Number.isFinite(Number(o.gcCases)) && Number(o.gcCases) > 0) {
    items.push({
      key: 'discipline',
      rank: 6,
      severity: 'waiting',
      text: Number(o.gcCases) + ' discipline case' + (Number(o.gcCases) === 1 ? '' : 's') + ' forwarded to you',
      sub: 'on your committee desk',
      page: 'committee',
      arg: null,
    });
  }

  items.sort((a, b) => (a.rank - b.rank)
    || ((a.days == null ? 99 : a.days) - (b.days == null ? 99 : b.days))
    || String(a.text).localeCompare(String(b.text)));

  return { state: items.length ? 'items' : 'clear', items, message: items.length ? '' : FOCUS_EMPTY };
}

// Zone 2. The counts, as doors, in one strip - the same numbers the stat cards carried.
// Returns [] on a refused read: a zero that means "we do not know" is not displayed (A1).
export function workCounts(input) {
  const o = input || {};
  if (o.readFailed) return [];
  const claims = Array.isArray(o.claims) ? o.claims : [];
  const open = claims.filter((c) => c && !NOT_OPEN.includes(c.status)).length;
  const exposure = claims.reduce((sum, c) => sum + (parseFloat(c && c.amountClaimed) || 0), 0);
  const out = [
    { key: 'open', label: 'Open claims', value: open, page: 'claims' },
    { key: 'total', label: 'All claims', value: claims.length, page: 'claims' },
  ];
  if (isGC(o.user) && o.elevationStage) {
    out.push({
      key: 'sentup',
      label: 'Sent up to you',
      value: claims.filter((c) => c && c.status === o.elevationStage).length,
      page: 'claims',
      arg: { filterStatus: o.elevationLabel || 'sent up' },
    });
  }
  out.push({
    key: 'exposure',
    label: 'Total exposure',
    value: exposure >= 1000 ? '$' + (exposure / 1000).toFixed(0) + 'K' : '$' + exposure.toFixed(0),
    page: 'claims',
  });
  return out;
}
